import {IRestrictionsService} from "./restrictions.service.interface";
import {ServiceBase} from "../ServiceBase";
import {GameInfo} from "../games/game-info";
import {IRestrictionsStrategy} from "./strategies/restrictions.strategy.interface";
import {AuthorizedUserRestrictionsStrategy} from "./strategies/authorized-user-restrictions.strategy";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {AnonymousUserRestrictionsStrategy} from "./strategies/anonymous-user-restrictions.strategy";
import {makeObservable, observable} from "mobx";
import {IRestrictionValue} from "./values/restriction-value.interface";

export class RestrictionsService extends ServiceBase implements IRestrictionsService {
    constructor(services: IServiceFactoryExtended) {
        super(services);
        this._anonymousUserRestrictions = new AnonymousUserRestrictionsStrategy(services);
        this._authorizedUserRestrictions = new AuthorizedUserRestrictionsStrategy(services);
        this._currentStrategy = this._anonymousUserRestrictions;
        makeObservable(this);

        this.reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if(isAuthorized) {
                    this._currentStrategy = this._authorizedUserRestrictions;
                } else {
                    this._currentStrategy = this._anonymousUserRestrictions;
                }
            }, {
                fireImmediately: true
            });

        this.reaction(() => this.isApplicationRestricted, (isAppRestricted) => {
            if(isAppRestricted) {
                this._onApplicationRestricted();
            }
        }, {
            fireImmediately: true
        });

        if(this.services.device.hybrid.inCasino && this.services.device.os.isIOS) {
            this.services.sdk.events.onHybridInfoComplete(this._onHybridInfoComplete);
        }

    }

    @observable.ref
    private _currentStrategy: IRestrictionsStrategy

    private readonly _authorizedUserRestrictions: AuthorizedUserRestrictionsStrategy;
    private readonly _anonymousUserRestrictions: AnonymousUserRestrictionsStrategy;

    getGenericRestrictionMessage(): string {
        return this.services.translation.translate('100200');
    }

    getGameRestriction(gameInfo: GameInfo): IRestrictionValue {
        return this._currentStrategy.restrictions.getGameRestriction(gameInfo);
    }

    private get isApplicationRestricted(): boolean {
        return this._currentStrategy.restrictions.isProductPackageRestrictedForOpen()
                || this._currentStrategy.restrictions.isSubBrandRestrictedForOpen();
    }

    //For debugging purpose
    printRestrictions(): void {
        this._currentStrategy.restrictions.printRestrictions();
    }

    private _onApplicationRestricted(): void {
        const translation = this.services.translation;
        const titleText = translation.translate('RESTRICTION_FOR_USE_TITLE');
        const restrictionText = translation.translate('RESTRICTION_FOR_USE_TEXT');

        this.services.dialog.showSimpleTextModal({
            title: titleText,
            text: restrictionText,
            hideCloseButton: true,
            disableFullscreenMobile: false
        });

        if(this._currentStrategy.restrictions.isProductPackageRestrictedForOpen()) {
            this.services.analytics.sendAnalytics('Restrictions', 'OpenPackageRestriction', this.services.sdk.getProductPackageId().toString());
        } else if(this._currentStrategy.restrictions.isSubBrandRestrictedForOpen()) {
            this.services.analytics.sendAnalytics('Restrictions', 'OpenSubBrandRestriction', this.services.sdk.getSubBrandID().toString());
        }
    }

    private _onHybridInfoComplete = () => {
        const country = this.services.sdk.getCountryIso3()?.toUpperCase();
        const appID = this.services.hybrid.getAppleID();
        const permittedMap = this.services.configuration.servicesConfigs.Restrictions.hybridPermittedMap;
        const permittedCountries = permittedMap[appID] || permittedMap.DEFAULT;

        if(permittedCountries !== 'All' && permittedCountries !== country) {
            this.services.analytics.sendAnalytics('Restrictions', 'hybrid_restriction_country', 'restricted_country', country);
            const translation = this.services.translation;
            const titleText = translation.translate('RESTRICTION_FOR_USE_TITLE');
            const restrictionText = translation.translate('RESTRICTION_FOR_USE_TEXT');

            this.services.dialog.showSimpleTextModal({
                title: titleText,
                text: restrictionText,
                hideCloseButton: true,
                disableFullscreenMobile: false
            });
        }
    }
}